import React, { FC, useContext, useEffect, useMemo } from 'react';
import * as R from 'ramda';

import { FormFieldConfiguration } from '../../../Settings/views/Customers/types';

export const FormContext = React.createContext<{
  config: Record<string, FormFieldConfiguration>;
  setFieldIsValid: (fieldName: string, isValid: boolean) => void;
}>({ config: {}, setFieldIsValid: () => null });

export const ConfigurableForm: FC<{
  config: Record<string, FormFieldConfiguration>;
  setFieldIsValid: (fieldName: string, isValid: boolean) => void;
}> = ({ children, config, setFieldIsValid }) => {
  return (
    <FormContext.Provider value={{ config, setFieldIsValid }}>
      {children}
    </FormContext.Provider>
  );
};

export const Field: FC<{
  name: string;
  wrapper?: any;
  wrapperProps?: any;
}> = ({ name, children, wrapper: Wrapper = React.Fragment, wrapperProps }) => {
  const { config, setFieldIsValid } = useContext(FormContext);
  const {
    [name]: { enabled, required, label } = {
      enabled: true,
      required: false,
      label: '',
    },
  } = config;

  const isNonPrimitive = child => !['string', 'number'].includes(typeof child);
  const child = useMemo(() => {
    return React.Children.toArray(children)?.[0];
  }, [children]);

  // allow custom validation to be passed on from config
  const validate = child => {
    const { value = '', error, helperText = 'Invalid value' } =
      child?.props ?? {};
    const check = typeof value === 'string' ? !value.trim() : !value;
    if (required && check) return 'Required field';
    if (error) return helperText;
    return '';
  };

  useEffect(() => {
    if (enabled) {
      setFieldIsValid(name, !validate(child));
    }
  }, [
    (child as any)?.props.value,
    (child as any)?.props.error,
    (child as any)?.props.helperText,
    enabled,
  ]);

  if (!enabled) return null;

  return (
    <Wrapper {...wrapperProps}>
      {R.map(
        R.when(
          isNonPrimitive,
          R.pipe(
            R.assocPath(['props', 'required'], required),
            R.when(() => !!label, R.assocPath(['props', 'label'], label)),
            R.when(
              validate,
              R.pipe(R.assocPath(['props', 'error'], true), child =>
                R.assocPath(['props', 'helperText'], validate(child), child),
              ),
            ),
          ),
        ),
      )(React.Children.toArray(children))}
    </Wrapper>
  );
};
